/** Ключ JSON-настройки приложения Битрикс24 (app.option) для входящих в смарт-процесс */
export const DOKI_INCOMING_SP_OPTION_KEY = "doki_incoming_sp_config";

/** События ДО, при которых создаётся элемент смарт-процесса (один раз на документооборот) */
export const INCOMING_SP_CREATE_EVENT_TYPES = new Set(["DocflowCreateSuccess"]);

export type IncomingSpFieldKey =
  | "doc_id"
  | "docflow_id"
  | "doc_url"
  | "sender_id"
  | "sender_name"
  | "sender_inn"
  | "sender_kpp"
  | "doc_type"
  | "doc_title"
  | "doc_date";

export interface IncomingSpFieldDef {
  key: IncomingSpFieldKey;
  fieldName: string;
  userTypeId: string;
  editFormLabel: string;
  listColumnLabel: string;
  xmlId: string;
}

export interface IncomingSpFieldMeta {
  id: number;
  fieldName: string;
}

/** Сохраняется в app.option как JSON */
export interface IncomingSpConfigStored {
  entityTypeId: number;
  fields: Partial<Record<IncomingSpFieldKey, IncomingSpFieldMeta>>;
}

export interface IncomingSpSmartProcessType {
  entityTypeId: number;
  title: string;
  typeInternalId: number;
}

/**
 * ENTITY_ID для userfieldconfig.* по смарт-процессу: CRM_{id},
 * где id — поле `id` из элемента crm.type.list (не путать с entityTypeId).
 */
export function incomingSpUserfieldconfigEntityId(typeInternalId: number): string {
  return `CRM_${typeInternalId}`;
}

/** Имя UF в формате, который ожидает userfieldconfig.add для SPA */
export function incomingSpFieldNameForUserfieldconfig(
  typeInternalId: number,
  def: IncomingSpFieldDef
): string {
  const tail = def.fieldName.startsWith("UF_CRM_")
    ? def.fieldName.slice("UF_CRM_".length)
    : def.fieldName;
  return `UF_CRM_${typeInternalId}_${tail}`;
}

export function parseIncomingSpConfigFromOption(raw: unknown): IncomingSpConfigStored | null {
  let obj: unknown = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      obj = JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== "object") {
    return null;
  }
  const o = obj as Record<string, unknown>;
  const entityTypeId = Number(o.entityTypeId);
  const fields = o.fields;
  if (!Number.isFinite(entityTypeId) || entityTypeId <= 0 || !fields || typeof fields !== "object") {
    return null;
  }
  return {
    entityTypeId,
    fields: fields as IncomingSpConfigStored["fields"],
  };
}

export function parseCrmTypeListResult(result: unknown): IncomingSpSmartProcessType[] {
  const r = result as { types?: unknown } | null | undefined;
  const types = r && typeof r === "object" && Array.isArray(r.types) ? r.types : [];
  return types
    .map((t: Record<string, unknown>) => ({
      typeInternalId: Number(t?.id),
      entityTypeId: Number(t?.entityTypeId),
      title:
        String(t?.title ?? t?.TITLE ?? "").trim() || `Смарт-процесс ${t?.entityTypeId}`,
    }))
    .filter(
      (t) =>
        Number.isFinite(t.entityTypeId) &&
        t.entityTypeId > 0 &&
        Number.isFinite(t.typeInternalId) &&
        t.typeInternalId > 0
    );
}

export const DOKI_INCOMING_SP_FIELD_DEFS: IncomingSpFieldDef[] = [
  {
    key: "doc_id",
    fieldName: "UF_CRM_DOKI_IN_DOC_ID",
    userTypeId: "string",
    editFormLabel: "Доки: ИД документа",
    listColumnLabel: "Доки: ИД документа",
    xmlId: "DOKI_IN_DOC_ID",
  },
  {
    key: "docflow_id",
    fieldName: "UF_CRM_DOKI_IN_DOCFLOW_ID",
    userTypeId: "string",
    editFormLabel: "Доки: ИД документооборота",
    listColumnLabel: "Доки: ИД ДО",
    xmlId: "DOKI_IN_DOCFLOW_ID",
  },
  {
    key: "doc_url",
    fieldName: "UF_CRM_DOKI_IN_DOC_URL",
    userTypeId: "url",
    editFormLabel: "Доки: ссылка на документ (1cdocs)",
    listColumnLabel: "Доки: ссылка",
    xmlId: "DOKI_IN_DOC_URL",
  },
  {
    key: "sender_id",
    fieldName: "UF_CRM_DOKI_IN_SENDER_ID",
    userTypeId: "string",
    editFormLabel: "Доки: ИД отправителя",
    listColumnLabel: "Доки: ИД отправителя",
    xmlId: "DOKI_IN_SENDER_ID",
  },
  {
    key: "sender_name",
    fieldName: "UF_CRM_DOKI_IN_SENDER_NAME",
    userTypeId: "string",
    editFormLabel: "Доки: название отправителя",
    listColumnLabel: "Доки: отправитель",
    xmlId: "DOKI_IN_SENDER_NAME",
  },
  {
    key: "sender_inn",
    fieldName: "UF_CRM_DOKI_IN_SENDER_INN",
    userTypeId: "string",
    editFormLabel: "Доки: ИНН отправителя",
    listColumnLabel: "Доки: ИНН отправителя",
    xmlId: "DOKI_IN_SENDER_INN",
  },
  {
    key: "sender_kpp",
    fieldName: "UF_CRM_DOKI_IN_SENDER_KPP",
    userTypeId: "string",
    editFormLabel: "Доки: КПП отправителя",
    listColumnLabel: "Доки: КПП отправителя",
    xmlId: "DOKI_IN_SENDER_KPP",
  },
  {
    key: "doc_type",
    fieldName: "UF_CRM_DOKI_IN_DOC_TYPE",
    userTypeId: "string",
    editFormLabel: "Доки: тип документа",
    listColumnLabel: "Доки: тип документа",
    xmlId: "DOKI_IN_DOC_TYPE",
  },
  {
    key: "doc_title",
    fieldName: "UF_CRM_DOKI_IN_DOC_TITLE",
    userTypeId: "string",
    editFormLabel: "Доки: название документа",
    listColumnLabel: "Доки: название документа",
    xmlId: "DOKI_IN_DOC_TITLE",
  },
  {
    key: "doc_date",
    fieldName: "UF_CRM_DOKI_IN_DOC_DATE",
    userTypeId: "date",
    editFormLabel: "Доки: дата документа",
    listColumnLabel: "Доки: дата документа",
    xmlId: "DOKI_IN_DOC_DATE",
  },
];
